import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/axios';

const DEEP_LINKS = [
  { value: '', label: 'Just open the app' },
  { value: 'home', label: 'Home' },
  { value: 'daily-jobs', label: 'Daily Jobs For You' },
  { value: 'profile', label: 'Profile' },
];

const TITLE_MAX = 20;
const MESSAGE_MAX = 100;

const STATUS_STYLES = {
  queued: 'bg-amber-50 text-amber-600',
  sending: 'bg-blue-50 text-blue-600',
  sent: 'bg-[#ECFDF5] text-[#10B981]',
  failed: 'bg-[#FEF2F2] text-[#EF4444]',
};
const STATUS_ORDER = ['queued', 'sending', 'sent', 'failed'];
const STATUS_RANK = (s) => {
  const i = STATUS_ORDER.indexOf(s);
  return i === -1 ? 99 : i;
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const FIELD = 'w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white text-gray-700 hover:border-purple-300 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const PILL_FIELD = 'w-full border border-gray-200 rounded-full px-4 py-2 text-sm bg-white text-gray-700 hover:border-purple-300 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const SELECT = `${PILL_FIELD} appearance-none cursor-pointer pr-9`;
const CAPTION = 'block text-xs font-semibold text-gray-500 mb-1 ml-1';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const presetRange = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { from: iso(from), to: iso(to) };
};
const DATE_PRESETS = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
];

const compareSort = (av, bv, dir) => {
  const aEmpty = av === null || av === undefined || av === '';
  const bEmpty = bv === null || bv === undefined || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const r =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? r : -r;
};

function SortTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      title="Click to sort"
      className={`text-left px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap transition hover:text-[#DB2777] ${active ? 'text-[#DB2777]' : ''}`}
    >
      {label}
      <span className={`ml-1 ${active ? '' : 'opacity-40'}`}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

function SelectBox({ caption, value, onChange, children }) {
  return (
    <div>
      <label className={CAPTION}>{caption}</label>
      <div className="relative">
        <select value={value} onChange={onChange} className={SELECT}>{children}</select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-purple-500 text-[10px]">▼</span>
      </div>
    </div>
  );
}

function Pager({ page, lastPage, onPage }) {
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(lastPage, page + 2); p++) nums.push(p);
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center gap-1">
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      {nums.map((p) => (
        <button
          key={p}
          onClick={() => onPage(p)}
          className={`${btn} ${p === page ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm' : 'bg-white hover:bg-purple-50'}`}
        >
          {p}
        </button>
      ))}
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(page + 1)}>Next</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(lastPage)}>»</button>
    </div>
  );
}

function EmptyState({ icon, title, hint }) {
  return (
    <div className="py-14 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center text-2xl">{icon}</div>
      <p className="mt-3 font-semibold text-gray-700">{title}</p>
      {hint && <p className="text-sm text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function SkeletonRows({ cols, rows = 4 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="border-t border-gray-50">
      {Array.from({ length: cols }).map((__, c) => (
        <td key={c} className="px-4 py-4"><div className="h-4 rounded-full bg-gray-100 animate-pulse" style={{ width: `${55 + ((i + c) % 4) * 10}%` }} /></td>
      ))}
    </tr>
  ));
}

function Counter({ value, max }) {
  const near = value >= max * 0.9;
  return (
    <span className={`text-xs font-semibold tabular-nums ${near ? 'text-amber-600' : 'text-gray-400'}`}>
      {value}/{max}
    </span>
  );
}

export default function NotificationCampaignsPanel() {
  const [form, setForm] = useState({ title: '', message: '', deep_link: '', city: '' });
  const [recipients, setRecipients] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // History filters / sorting / paging
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detail, setDetail] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const payload = () => ({
    title: form.title.trim(),
    message: form.message.trim(),
    deep_link: form.deep_link || null,
    city: form.city.trim() || null,
  });

  const errText = (e, fallback) => {
    const errors = e.response?.data?.errors;
    return errors ? Object.values(errors).flat().join(' ') : e.response?.data?.message || fallback;
  };

  const HISTORY_ERR = 'Could not load the history. Check your connection and try again.';

  const loadHistory = useCallback(async (silent = false) => {
    if (!silent) setHistoryLoading(true);
    try {
      const res = await api.get('/admin/notification-campaigns');
      setHistory(Array.isArray(res.data) ? res.data : []);
      setHistoryLoaded(true);
      setErr((e) => (e === HISTORY_ERR ? '' : e));
    } catch (e) {
      if (!silent) setErr(HISTORY_ERR);
    } finally {
      if (!silent) setHistoryLoading(false);
    }
  }, []);

  const loadPreview = useCallback(async (city) => {
    setPreviewing(true);
    try {
      const res = await api.post('/admin/notification-campaigns/preview', { city: city?.trim() || null });
      setRecipients(res.data.recipients);
    } catch {
      setRecipients(null);
    } finally {
      setPreviewing(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadPreview('');
  }, [loadHistory, loadPreview]);

  // refresh the count shortly after the city filter changes
  useEffect(() => {
    const t = setTimeout(() => loadPreview(form.city), 500);
    return () => clearTimeout(t);
  }, [form.city, loadPreview]);

  // while a campaign is queued or sending, keep refreshing the history
  useEffect(() => {
    if (!history.some((c) => c.status === 'queued' || c.status === 'sending')) return;
    const t = setInterval(() => loadHistory(true), 10000);
    return () => clearInterval(t);
  }, [history, loadHistory]);

  useEffect(() => { setPage(1); }, [search, statusFilter, cityFilter, dateFrom, dateTo, sort, pageSize]);

  const valid = form.title.trim() && form.message.trim();

  // eslint-disable-next-line no-unused-vars
  const sendTest = async () => {
    setTesting(true); setErr(''); setMsg('');
    try {
      const res = await api.post('/admin/notification-campaigns/test', payload());
      setMsg(res.data.message || 'Test sent to your own device.');
    } catch (e) {
      setErr(errText(e, 'Test send failed.'));
    } finally {
      setTesting(false);
    }
  };

  const send = async (confirm = false) => {
    setSending(true); setErr(''); setMsg('');
    try {
      await api.post('/admin/notification-campaigns', { ...payload(), ...(confirm ? { confirm: true } : {}) });
      setMsg('Campaign queued. It will go out shortly.');
      setForm((f) => ({ ...f, title: '', message: '' }));
      loadHistory();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.requires_confirmation) {
        const n = e.response.data.recipients;
        if (window.confirm(`This will send a push notification to ${n} people. This cannot be undone. Send now?`)) {
          await send(true);
          return;
        }
      } else {
        setErr(errText(e, 'Could not send the campaign. Try again.'));
      }
    } finally {
      setSending(false);
    }
  };

  const onSend = () => {
    if (!valid) return;
    send(false);
  };

  const onSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const cityOf = (c) => c.city || 'All';
  const rate = (c) => (Number(c.total_recipients) > 0 ? (Number(c.sent_count) / Number(c.total_recipients)) * 100 : 0);

  const cityOptions = useMemo(
    () => Array.from(new Set(history.map((c) => c.city).filter(Boolean))).sort(),
    [history]
  );
  const statusOptions = useMemo(() => {
    const present = Array.from(new Set(history.map((c) => c.status).filter(Boolean)));
    return present.sort((a, b) => STATUS_RANK(a) - STATUS_RANK(b));
  }, [history]);

  // Everything except status, so the status tabs can show their own counts
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return history.filter((c) => {
      if (cityFilter === '__all__' && c.city) return false;
      if (cityFilter && cityFilter !== '__all__' && c.city !== cityFilter) return false;
      if (dateFrom || dateTo) {
        if (!c.created_at) return false;
        const d = new Date(c.created_at).setHours(0, 0, 0, 0);
        if (dateFrom && d < new Date(dateFrom).setHours(0, 0, 0, 0)) return false;
        if (dateTo && d > new Date(dateTo).setHours(23, 59, 59, 999)) return false;
      }
      if (!q) return true;
      return c.title?.toLowerCase().includes(q) || c.message?.toLowerCase().includes(q);
    });
  }, [history, search, cityFilter, dateFrom, dateTo]);

  const statusCounts = useMemo(() => {
    const m = {};
    baseFiltered.forEach((c) => { m[c.status] = (m[c.status] || 0) + 1; });
    return m;
  }, [baseFiltered]);

  const filtered = useMemo(
    () => (statusFilter ? baseFiltered.filter((c) => c.status === statusFilter) : baseFiltered),
    [baseFiltered, statusFilter]
  );

  const sortValue = (c, key) => {
    switch (key) {
      case 'title': return c.title || '';
      case 'message': return c.message || '';
      case 'city': return cityOf(c);
      case 'status': return STATUS_RANK(c.status);
      case 'audience': return Number(c.total_recipients) || 0;
      case 'sent': return Number(c.sent_count) || 0;
      case 'skipped': return Number(c.skipped_count) || 0;
      case 'rate': return rate(c);
      case 'created': return c.created_at ? new Date(c.created_at).getTime() : null;
      default: return null;
    }
  };

  const sorted = useMemo(
    () => filtered.slice().sort((a, b) => compareSort(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir)),
    [filtered, sort]
  );

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cur = Math.min(page, pages);
  const paged = sorted.slice((cur - 1) * pageSize, cur * pageSize);

  const totalSent = history.reduce((s, c) => s + (Number(c.sent_count) || 0), 0);
  const totalSkipped = history.reduce((s, c) => s + (Number(c.skipped_count) || 0), 0);
  const totalAudience = history.reduce((s, c) => s + (Number(c.total_recipients) || 0), 0);
  const overallRate = totalAudience > 0 ? ((totalSent / totalAudience) * 100).toFixed(1) : '0.0';

  const cards = [
    { title: 'Campaigns', value: history.length, icon: '📣', tone: 'from-[#7C3AED] to-[#A78BFA]' },
    { title: 'Notifications sent', value: totalSent, icon: '📲', tone: 'from-[#10B981] to-[#34D399]' },
    { title: 'Skipped', value: totalSkipped, icon: '🌙', tone: 'from-[#F59E0B] to-[#FBBF24]' },
    { title: 'Delivery rate', value: `${overallRate}%`, icon: '🎯', tone: 'from-[#DB2777] to-[#F472B6]' },
  ];

  const clearFilters = () => { setSearch(''); setStatusFilter(''); setCityFilter(''); setDateFrom(''); setDateTo(''); };

  const chips = [
    search && { k: 'q', text: `Search: ${search}`, clear: () => setSearch('') },
    statusFilter && { k: 's', text: cap(statusFilter), clear: () => setStatusFilter('') },
    cityFilter && { k: 'c', text: cityFilter === '__all__' ? 'Everyone (no city)' : cityFilter, clear: () => setCityFilter('') },
    dateFrom && { k: 'df', text: `From ${dateFrom}`, clear: () => setDateFrom('') },
    dateTo && { k: 'dt', text: `To ${dateTo}`, clear: () => setDateTo('') },
  ].filter(Boolean);

  const activePreset = DATE_PRESETS.find((p) => {
    const r = presetRange(p.days);
    return dateFrom === r.from && dateTo === r.to;
  })?.days;

  const tabs = [
    { value: '', label: 'All', count: baseFiltered.length },
    ...statusOptions.map((s) => ({ value: s, label: cap(s), count: statusCounts[s] || 0 })),
  ];

  const cols = [
    ['Title', 'title'], ['Message', 'message'], ['City', 'city'], ['Status', 'status'],
    ['Audience', 'audience'], ['Sent', 'sent'], ['Skipped', 'skipped'], ['Delivered', 'rate'], ['Created', 'created'],
  ];

  return (
    <div className="space-y-5">
      {/* Compose */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white flex items-center justify-center text-lg shadow-sm">📣</div>
          <div>
            <h3 className="font-bold text-[#0D1B3D] text-lg leading-tight">Send a notification to job seekers</h3>
            <p className="text-xs text-gray-500">
              Reaches job seekers with the app installed and push turned on. People in quiet hours (10 PM to 8 AM) are skipped.
            </p>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-4">
            {err && (
              <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between gap-3">
                <span>{err}</span>
                {err === HISTORY_ERR && <button type="button" onClick={() => loadHistory()} className="font-semibold underline">Retry</button>}
              </div>
            )}
            {msg && <div className="bg-green-50 border border-green-100 text-green-700 rounded-xl px-4 py-3 text-sm">{msg}</div>}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500">Title</label>
                <Counter value={form.title.length} max={TITLE_MAX} />
              </div>
              <input type="text" maxLength={TITLE_MAX} value={form.title} onChange={set('title')}
                placeholder="e.g. New jobs added today" className={FIELD} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500">Message</label>
                <Counter value={form.message.length} max={MESSAGE_MAX} />
              </div>
              <textarea rows={3} maxLength={MESSAGE_MAX} value={form.message} onChange={set('message')}
                placeholder="Write the message candidates will see" className={FIELD} />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">City</label>
              <input type="text" value={form.city} onChange={set('city')}
                placeholder="Leave empty to reach everyone, or type a city such as Bengaluru" className={FIELD} />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-full pl-3 pr-4 py-1.5 text-sm text-purple-700">
                <span>👥</span>
                <span>Recipients</span>
                <b className="text-[#7C3AED]">{previewing ? '…' : recipients ?? '—'}</b>
              </div>

              {/* <button type="button" onClick={sendTest} disabled={!valid || testing}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-purple-200 text-[#7C3AED] bg-purple-50 hover:bg-purple-100 disabled:opacity-50">
                {testing ? 'Sending…' : 'Send test to me'}
              </button> */}

              <button type="button" onClick={onSend} disabled={!valid || sending || recipients === 0}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-7 py-2.5 rounded-full text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
                {sending ? 'Sending…' : recipients ? `Send to ${recipients} people` : 'Send notification'}
              </button>
            </div>
            {!valid && <p className="text-xs text-gray-400">Add a title and a message to enable sending.</p>}
            {recipients === 0 && <p className="text-xs text-amber-600">No job seekers match this city, so there is nobody to send to.</p>}
          </div>

          {/* Phone-style live preview */}
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold text-gray-500 mb-2">Live preview</p>
            <div className="mx-auto max-w-xs rounded-[2rem] bg-gradient-to-b from-gray-900 to-gray-800 p-3 shadow-lg">
              <div className="rounded-[1.5rem] bg-gradient-to-br from-purple-200 via-pink-100 to-purple-100 px-3 pt-8 pb-10">
                <div className="bg-white/90 backdrop-blur rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-5 h-5 rounded-md bg-gradient-to-br from-[#7C3AED] to-[#DB2777]" />
                    <span className="text-[10px] font-semibold text-gray-500">PlanB Jobs · now</span>
                  </div>
                  <p className={`text-sm font-bold break-words ${form.title ? 'text-gray-800' : 'text-gray-300'}`}>{form.title || 'Your title'}</p>
                  <p className={`text-xs mt-0.5 break-words ${form.message ? 'text-gray-600' : 'text-gray-300'}`}>{form.message || 'Your message will appear here'}</p>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-gray-400 text-center mt-2">Sent notifications cannot be recalled.</p>
          </div>
        </div>
      </div>

      {/* History summary */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.title} className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.tone} text-white flex items-center justify-center text-xl shadow-sm shrink-0`}>{c.icon}</div>
            <div>
              <p className="text-2xl font-extrabold text-[#0D1B3D] leading-tight">{typeof c.value === 'number' ? c.value.toLocaleString('en-IN') : c.value}</p>
              <p className="text-sm font-semibold text-gray-600">{c.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* History filters */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#0D1B3D]">Filter history</h3>
          {chips.length > 0 && (
            <button type="button" onClick={clearFilters}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition">
              ✕ Clear all
            </button>
          )}
        </div>

        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or message" className={`${PILL_FIELD} pl-10`} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <SelectBox caption="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
          </SelectBox>
          <SelectBox caption="City" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <option value="">All campaigns</option>
            <option value="__all__">Sent to everyone (no city)</option>
            {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </SelectBox>
          <div className="flex flex-wrap gap-2 items-end pb-0.5">
            {DATE_PRESETS.map((p) => (
              <button key={p.days} type="button"
                onClick={() => { const r = presetRange(p.days); setDateFrom(r.from); setDateTo(r.to); }}
                className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition ${
                  activePreset === p.days
                    ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                    : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={CAPTION}>Created from</label>
            <input type="date" className={`${PILL_FIELD} w-44`} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className={CAPTION}>Created to</label>
            <input type="date" className={`${PILL_FIELD} w-44`} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {chips.map((c) => (
              <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                {c.text}
                <button type="button" onClick={c.clear} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 border-b border-gray-100 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.label}
            type="button"
            onClick={() => setStatusFilter(t.value)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition inline-flex items-center gap-2 whitespace-nowrap ${
              statusFilter === t.value ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
            <span className={`text-[11px] font-bold h-5 min-w-[1.25rem] px-1.5 rounded-full flex items-center justify-center ${
              statusFilter === t.value ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* History table */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            Showing <span className="font-semibold text-purple-600">{sorted.length}</span> of {history.length} campaigns. Click a column heading to sort.
          </p>
          <button type="button" onClick={() => loadHistory()} className="text-xs font-semibold text-[#7C3AED] hover:underline whitespace-nowrap">
            {historyLoading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>{cols.map(([h, k]) => <SortTh key={k} label={h} sortKey={k} sort={sort} onSort={onSort} />)}</tr>
            </thead>
            <tbody>
              {!historyLoaded ? (
                <SkeletonRows cols={9} />
              ) : paged.length === 0 ? (
                <tr><td colSpan={9}>
                  <EmptyState
                    icon={history.length === 0 ? '📭' : '🔎'}
                    title={history.length === 0 ? 'No notifications sent yet' : 'No campaigns match your filters'}
                    hint={history.length === 0 ? 'Write a title and message above to send your first one.' : 'Clear a filter or widen the date range.'}
                  />
                </td></tr>
              ) : paged.map((c) => {
                const r = rate(c);
                return (
                  <tr key={c.id} className="border-t border-gray-50 hover:bg-purple-50/60 transition-colors align-top">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setDetail(c)} className="font-semibold text-[#7C3AED] hover:underline text-left">{c.title}</button>
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <p className="text-gray-600 leading-snug break-words" title={c.message}
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {c.message}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${c.city ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                        {cityOf(c)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[c.status] || 'bg-gray-100 text-gray-500'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{c.total_recipients}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-[#10B981]">{c.sent_count}</td>
                    <td className="px-4 py-3 tabular-nums text-amber-600">{c.skipped_count}</td>
                    <td className="px-4 py-3 w-40">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777]" style={{ width: `${Math.min(100, r)}%` }} />
                        </div>
                        <span className="w-12 text-right text-xs font-bold text-[#7C3AED] tabular-nums">{r.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(c.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-3">
            <span>Page {cur} of {pages}</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
          <Pager page={cur} lastPage={pages} onPage={setPage} />
        </div>
      </div>

      {/* Campaign detail popup */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-white break-words">{detail.title}</h3>
                <p className="text-purple-100 text-xs mt-0.5">Created {fmtDateTime(detail.created_at)}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-purple-100 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 mb-1">Message</p>
                <p className="whitespace-pre-wrap break-words text-gray-700 bg-purple-50/50 border border-purple-100 rounded-xl p-3">{detail.message}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['City', cityOf(detail)],
                  ['Status', cap(detail.status)],
                  ['Audience', detail.total_recipients],
                  ['Sent', detail.sent_count],
                  ['Skipped', detail.skipped_count],
                  ['Delivered', `${rate(detail).toFixed(0)}%`],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-gray-50 p-3">
                    <p className="text-xs text-gray-400">{k}</p>
                    <p className="text-gray-800 font-semibold">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}